import React, { useState } from 'react';
import axios from 'axios';
import { Send, Loader2, Mail, Instagram, Linkedin, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Section from '../../components/v2/Section';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactV2() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        message: form.message,
        service_category: 'General enquiry (v2 contact form)',
      });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong, please email hello@audioandco.com directly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title="Contact Audio & Co, Podcast Production & Content Agency"
        description="Contact Audio & Co. about podcast management, book publishing, audiobooks or a business website built for SEO. Jo replies personally."
        url="https://audioandco.com/contact"
      />

      <Section bg="bg" innerClassName="!py-16 sm:!py-24" testId="contact-page">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Get in touch</p>
            <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl leading-[1.1] tracking-tight mt-3">
              Got a question? We’re happy to chat.
            </h1>
            <p className="text-lg text-[#475569] mt-5 leading-relaxed">
              If you want to enrol on the Pro Plan, please <Link to="/book" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">book a call</Link> instead, it’s faster.
              Otherwise drop us a message below and we’ll come back within one working day.
            </p>

            <div className="mt-8 space-y-3">
              <a href="mailto:hello@audioandco.com" className="flex items-center gap-3 text-[#475569] hover:text-[#1E2A44]">
                <span className="w-9 h-9 rounded-lg bg-white border border-[#E2E8F0] flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#BC516F]" />
                </span>
                hello@audioandco.com
              </a>
              <div className="flex gap-3">
                <a href="https://instagram.com/audioandco" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg bg-white border border-[#E2E8F0] flex items-center justify-center hover:bg-[#1E2A44] hover:text-white text-[#1E2A44] transition" aria-label="Instagram">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href="https://www.linkedin.com/in/jo-day" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg bg-white border border-[#E2E8F0] flex items-center justify-center hover:bg-[#1E2A44] hover:text-white text-[#1E2A44] transition" aria-label="LinkedIn">
                  <Linkedin className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-[14px] p-6 sm:p-8 shadow-[0_10px_30px_rgba(30,42,68,0.06)]">
            {sent ? (
              <div className="text-center py-8" data-testid="contact-sent">
                <div className="w-14 h-14 rounded-full bg-[#D9FBEA] flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-7 h-7 text-[#1E2A44]" />
                </div>
                <h2 className="font-display font-semibold text-[#1E2A44] text-xl">Thanks, message sent.</h2>
                <p className="text-[#64748B] mt-2">We’ll get back to you within one working day.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <Field label="Your name" value={form.name} onChange={v => setForm({...form, name: v})} required testId="contact-name" />
                <Field label="Email" type="email" value={form.email} onChange={v => setForm({...form, email: v})} required testId="contact-email" />
                <div>
                  <label className="block text-sm font-medium text-[#1E2A44] mb-1.5">Message</label>
                  <textarea
                    required
                    value={form.message}
                    onChange={e => setForm({...form, message: e.target.value})}
                    rows={5}
                    className="w-full bg-white border border-[#E2E8F0] rounded-[10px] px-4 py-3 text-[#1E2A44] focus:border-[#BC516F] focus:ring-2 focus:ring-[#BC516F]/15 outline-none resize-y"
                    data-testid="contact-message"
                  />
                </div>
                {error && <div className="text-sm text-[#BC516F]">{error}</div>}
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#BC516F] hover:bg-[#9A3A54] text-white font-semibold rounded-[10px] px-6 py-3 disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? 'Sending…' : 'Send message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </Section>
    </main>
  );
}

function Field({ label, value, onChange, type='text', required, testId }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#1E2A44] mb-1.5">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-white border border-[#E2E8F0] rounded-[10px] px-4 py-3 text-[#1E2A44] focus:border-[#BC516F] focus:ring-2 focus:ring-[#BC516F]/15 outline-none"
        data-testid={testId}
      />
    </div>
  );
}
